import { Button, Menu, MenuItem, MenuTrigger, notify } from '@affine/component';
import {
  generatePublicDocUrl,
  getSelectedNodes,
  useSharingUrl,
} from '@affine/core/components/hooks/affine/use-share-url';
import { useActiveBlocksuiteEditor } from '@affine/core/components/hooks/use-block-suite-editor';
import { ServerService } from '@affine/core/modules/cloud';
import { EditorService } from '@affine/core/modules/editor';
import { copyTextToClipboard } from '@affine/core/utils/clipboard';
import { useI18n } from '@affine/i18n';
import type { DocMode } from '@blocksuite/affine/model';
import { BlockIcon, EdgelessIcon, PageIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';

import * as styles from './copy-link-button.css';

export const CopyLinkButton = ({
  workspaceId,
  secondary,
  publicLink = false,
  disabled = false,
}: {
  secondary?: boolean;
  publicLink?: boolean;
  disabled?: boolean;
  workspaceId: string;
}) => {
  const t = useI18n();
  const server = useService(ServerService).server;

  const editor = useService(EditorService).editor;
  const currentMode = useLiveData(editor.mode$);
  const editorContainer = useLiveData(editor.editorContainer$);
  const [activeEditorContainer] = useActiveBlocksuiteEditor();

  const selectedLinkTarget = useMemo(() => {
    const activeEditorMatchesDoc =
      activeEditorContainer?.doc?.id === editor.doc.id ||
      activeEditorContainer?.page?.id === editor.doc.id;
    const sourceEditorContainer = activeEditorMatchesDoc
      ? activeEditorContainer
      : editorContainer;
    const mode = sourceEditorContainer?.mode ?? currentMode;
    const { blockIds, elementIds } = getSelectedNodes(
      sourceEditorContainer?.host || null,
      mode
    );

    return {
      blockIds,
      elementIds,
      mode,
    };
  }, [activeEditorContainer, currentMode, editor.doc.id, editorContainer]);

  const { onClickCopyLink } = useSharingUrl({
    workspaceId,
    pageId: editor.doc.id,
  });

  const onCopyPageLink = useCallback(() => {
    onClickCopyLink('page' as DocMode);
  }, [onClickCopyLink]);
  const onCopyEdgelessLink = useCallback(() => {
    onClickCopyLink('edgeless' as DocMode);
  }, [onClickCopyLink]);
  const onCopyBlockLink = useCallback(() => {
    onClickCopyLink(
      selectedLinkTarget.mode,
      selectedLinkTarget.blockIds,
      selectedLinkTarget.elementIds
    );
  }, [onClickCopyLink, selectedLinkTarget]);

  const onCopyLink = useCallback(() => {
    onClickCopyLink();
  }, [onClickCopyLink]);

  if (publicLink) {
    return (
      <Button
        variant="primary"
        disabled={disabled}
        data-testid="copy-public-doc-link"
        onClick={() => {
          void copyTextToClipboard(
            generatePublicDocUrl({
              baseUrl: server.baseUrl,
              workspaceId,
              pageId: editor.doc.id,
            })
          )
            .then(success => {
              if (success)
                notify.success({ title: t['Copied link to clipboard']() });
              else
                notify.error({
                  title: t['com.streamlain.share-menu.public-sharing.error'](),
                });
            })
            .catch(() =>
              notify.error({
                title: t['com.streamlain.share-menu.public-sharing.error'](),
              })
            );
        }}
      >
        {t['com.streamlain.share-menu.public-sharing.copy']()}
      </Button>
    );
  }

  return (
    <div
      className={clsx(styles.copyLinkContainerStyle, { secondary: secondary })}
    >
      <Button
        className={styles.copyLinkButtonStyle}
        onClick={onCopyLink}
        withoutHover
        variant={secondary ? 'secondary' : 'primary'}
      >
        <span
          className={clsx(styles.copyLinkLabelStyle, {
            secondary: secondary,
          })}
        >
          {t['com.streamlain.share-menu.public-sharing.internal-copy']()}
        </span>
      </Button>
      <Menu
        contentOptions={{
          align: 'end',
        }}
        items={
          <>
            <MenuItem
              prefixIcon={<PageIcon />}
              onSelect={onCopyPageLink}
              data-testid="share-link-menu-copy-page"
            >
              {t['com.streamlain.share-menu.copy.page']()}
            </MenuItem>
            <MenuItem
              prefixIcon={<EdgelessIcon />}
              onSelect={onCopyEdgelessLink}
              data-testid="share-link-menu-copy-edgeless"
            >
              {t['com.streamlain.share-menu.copy.edgeless']()}
            </MenuItem>
            <MenuItem
              prefixIcon={<BlockIcon />}
              onSelect={onCopyBlockLink}
              disabled={
                selectedLinkTarget.blockIds.length +
                  selectedLinkTarget.elementIds.length ===
                0
              }
            >
              {t['com.streamlain.share-menu.copy.block']()}
            </MenuItem>
          </>
        }
      >
        <MenuTrigger
          variant={secondary ? 'secondary' : 'primary'}
          className={clsx(styles.copyLinkTriggerStyle, {
            secondary: secondary,
          })}
          data-testid="share-menu-copy-link-button"
          suffixStyle={{ width: 20, height: 20 }}
          withoutHover
        />
      </Menu>
    </div>
  );
};
